import { useEffect, useMemo, useState } from 'react';
import type { PlatformKey, StoreResponse } from '@clarity/contract';
import { useStoreAnalytics, useUpsertStoreAnalytics } from '../queries/index.js';

/**
 * StoreAnalyticsEntry (clarity-owner-analytics) — the owner records this month's
 * operational figures for ONE branch: rating, delivery/prep time, and the
 * conversion funnel. Clarity doesn't reliably pull these from every platform, so
 * the owner enters what the platform dashboard shows; a future platform pull
 * upserts the same `(store, period)` row. The dashboard + advisor read them back.
 *
 * Kept compact and English (matching the dashboard surface). One row per branch
 * is enough for a monthly figure; opening hours get their own week grid.
 */

const PLATFORM_LABEL: Record<PlatformKey, string> = {
  talabat: 'Talabat',
  careem: 'Careem',
  mythings: 'MyThings',
};

function thisMonth(): { start: string; end: string; label: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const start = new Date(Date.UTC(y, m, 1)).toISOString();
  const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999)).toISOString();
  return { start, end, label: new Date(start).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) };
}

function numOrNull(v: string): number | null {
  const n = Number(v);
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
}

export function StoreAnalyticsEntry({ store }: { store: StoreResponse }) {
  const period = useMemo(() => thisMonth(), []);
  const list = useStoreAnalytics(store.id);
  const upsert = useUpsertStoreAnalytics(store.id);
  const records = list.data?.status === 200 ? list.data.body : [];
  const current = records.find((r) => r.periodStart === period.start && r.periodEnd === period.end) ?? null;

  const [rating, setRating] = useState('');
  const [ratingCount, setRatingCount] = useState('');
  const [delivery, setDelivery] = useState('');
  const [visits, setVisits] = useState('');
  const [conversions, setConversions] = useState('');

  // Seed the fields from the current record when it loads/changes.
  const currentId = current?.id ?? null;
  useEffect(() => {
    if (!current) return;
    setRating(current.ratingAvg != null ? String(current.ratingAvg) : '');
    setRatingCount(current.ratingCount != null ? String(current.ratingCount) : '');
    setDelivery(current.avgDeliveryMinutes != null ? String(current.avgDeliveryMinutes) : '');
    setVisits(current.menuVisits != null ? String(current.menuVisits) : '');
    setConversions(current.conversions != null ? String(current.conversions) : '');
    // Only reseed when the underlying record identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  return (
    <section className="card sa-entry" aria-label={`Operational metrics — ${store.name ?? ''}`}>
      <header className="sa-entry__head">
        <h3>
          Operational metrics · {store.name ?? PLATFORM_LABEL[store.platformKey as PlatformKey]}
          <span className="sa-entry__chan">{PLATFORM_LABEL[store.platformKey as PlatformKey]}</span>
        </h3>
        <p className="sa-entry__hint">
          {period.label} — enter what your {PLATFORM_LABEL[store.platformKey as PlatformKey]} dashboard
          shows. The dashboard and the advisor use these.
        </p>
      </header>
      <form
        className="sa-entry__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (upsert.isPending) return;
          upsert.mutate({
            params: { id: store.id },
            body: {
              periodStart: period.start,
              periodEnd: period.end,
              ratingAvg: numOrNull(rating),
              ratingCount: ratingCount.trim() === '' ? null : Math.round(Number(ratingCount)),
              avgDeliveryMinutes: numOrNull(delivery),
              menuVisits: visits.trim() === '' ? null : Math.round(Number(visits)),
              conversions: conversions.trim() === '' ? null : Math.round(Number(conversions)),
            },
          });
        }}
      >
        <label>Rating (0–5)<input type="number" min="0" max="5" step="0.1" value={rating} onChange={(e) => setRating(e.target.value)} /></label>
        <label># ratings<input type="number" min="0" step="1" value={ratingCount} onChange={(e) => setRatingCount(e.target.value)} /></label>
        <label>Delivery (min)<input type="number" min="0" step="1" value={delivery} onChange={(e) => setDelivery(e.target.value)} /></label>
        <label>Menu visits<input type="number" min="0" step="1" value={visits} onChange={(e) => setVisits(e.target.value)} /></label>
        <label>Orders (from visits)<input type="number" min="0" step="1" value={conversions} onChange={(e) => setConversions(e.target.value)} /></label>
        <button type="submit" disabled={upsert.isPending}>{current ? 'Update' : 'Save'}</button>
      </form>
      {current && (
        <p className="sa-entry__saved">
          Saved: {current.ratingAvg != null ? `★ ${current.ratingAvg}` : 'no rating'}
          {current.avgDeliveryMinutes != null ? ` · ${Math.round(current.avgDeliveryMinutes)} min` : ''}
          {current.menuVisits != null && current.conversions != null && current.menuVisits > 0
            ? ` · ${((current.conversions / current.menuVisits) * 100).toFixed(1)}% conversion`
            : ''}
        </p>
      )}
    </section>
  );
}
